import Container from "Components/Container/Container";
import { StatusHandler } from "Components/ApiNotification";
import usePost from "Hooks/usePost";
import { useNavigate, useLocation } from "react-router-dom";
import Spinner from "Components/RequestHandler/Spinner";

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [codeSent, setCodeSent] = useState(false);
  const inputRefs = useRef([]);
  const { postData, isLoading, isError, data } = usePost();

  useEffect(() => {
    // Try to get email from state or query params
    const params = new URLSearchParams(location.search);
    const emailFromParams = params.get("email");
    const emailFromState = location.state?.email;

    if (emailFromState) {
      setEmail(emailFromState);
    } else if (emailFromParams) {
      setEmail(emailFromParams);
    }
  }, [location]);

  const handleInputChange = (index, value) => {
    // Allow only digits
    if (/^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus to next input after entering a digit
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().slice(0, 6);

    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        if (i < 6) {
          newOtp[i] = pastedData[i];
        }
      }
      setOtp(newOtp);

      // Focus the input after the last pasted character
      if (pastedData.length < 6) {
        inputRefs.current[pastedData.length].focus();
      }
    }
  };

  const handleVerify = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 6) return;

    const res = await postData("auth/verify-email", {
      email,
      code: otpCode,
    });

    if (res?.success) {
      navigate("/");
    }
  };

  const handleSendCode = async () => {
    if (!email) return;

    const res = await postData("auth/send-verification", {
      email,
    });

    if (res?.success) {
      setCodeSent(true);
    }
  };

  const handleResendCode = async () => {
    if (!email) return;

    await postData("auth/send-verification", {
      email,
    });
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  return (
    <main>
      <Container>
        <div className="my-10 flex flex-col items-center justify-center">
          <h1 className="text-4xl font-medium text-center font-title">
            {t("auth.verifyEmailTitle", "Verify Your Email")}
          </h1>
          <StatusHandler error={isError} data={data} />
        </div>

        <div className="w-full max-w-md mx-auto">
          <div className="bg-white p-6 ">
            {!codeSent ? (
              <>
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-4">
                    {t(
                      "auth.enterEmailToReceiveCode",
                      "Please enter your email address to receive a verification code"
                    )}
                  </p>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t("auth.enterYourEmail", "Enter your email")}
                    className="w-full border border-primary rounded-md px-4 py-3 mb-4 text-lg"
                  />
                </div>
                <button
                  onClick={handleSendCode}
                  disabled={!email || !email.includes("@") || isLoading}
                  className="bg-primary w-full text-white px-4 py-3 rounded-md text-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <Spinner />
                  ) : (
                    t("auth.sendVerificationCode", "Send Verification Code")
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-2">
                    {t(
                      "auth.sentVerificationCodeTo",
                      "We've sent a verification code to:"
                    )}
                  </p>
                  <p className="text-lg font-semibold text-primary break-all">
                    {email}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {t(
                      "auth.enter6DigitCode",
                      "Please enter the 6-digit verification code"
                    )}
                  </p>
                </div>

                <div className="flex justify-center gap-2 mb-6">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      className="w-12 h-12 border border-primary rounded-md text-center text-xl font-bold"
                    />
                  ))}
                </div>

                <button
                  onClick={handleVerify}
                  disabled={otp.join("").length !== 6 || isLoading}
                  className="bg-primary w-full text-white px-4 py-3 rounded-md text-lg font-medium mb-4 hover:bg-primary-dark transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <Spinner />
                  ) : (
                    t("auth.verifyEmail", "Verify Email")
                  )}
                </button>

                <div className="text-center">
                  <p className="text-gray-600 mb-2">
                    {t("auth.didntReceiveCode", "Didn't receive the code?")}
                  </p>
                  <button
                    onClick={handleResendCode}
                    disabled={isLoading}
                    className="text-primary font-medium hover:underline"
                  >
                    {t("auth.resendCode", "Resend Code")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Container>
    </main>
  );
};

export default VerifyEmail;
